import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Loader2, History, Download, Search, ChevronLeft, ChevronRight, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { api, apiError } from "../lib/api";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const CATEGORY_FR = { get operational() { return tr("history:category.operational"); }, get financial() { return tr("history:category.financial"); } };
const CATEGORY_BADGE = {
  operational: "bg-cyan-500/15 text-cyan-600 border-cyan-500/30",
  financial: "bg-amber-500/15 text-amber-600 border-amber-500/30",
};
const PAGE_SIZE = 25;

const fmtActor = (ev) => (ev.actor_kind === "system" ? tr("history:panel.actorSystem")
  : ev.actor_kind === "unknown" ? tr("history:panel.actorUnknown") : ev.actor);

const fmtDetail = (ev) => {
  const m = ev.detail_meta;
  if (!m) return ev.detail || "";
  if (m.extra) return Object.entries(m.extra).map(([k, v]) => `${k} : ${v}`).join(", ");
  const parts = [];
  if (m.role) parts.push(tr("history:panel.detailRole", { role: tr(`common:roles.${m.role}`, { defaultValue: m.role }) }));
  if (m.email) parts.push(m.email);
  if (m.from != null && m.to != null) parts.push(`${m.from} → ${m.to}`);
  if (m.effective_at) parts.push(tr("history:panel.detailEffective", { date: m.effective_at }));
  if (m.type) parts.push(m.type);
  return parts.join(", ");
};
const ALL = "__all__";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(intlLocaleFor(i18n.language), {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

export const HistoryPanel = ({ workspaceId, role }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [offset, setOffset] = useState(0);

  const [category, setCategory] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const buildParams = useCallback(() => {
    const p = {};
    if (category !== ALL) p.category = category;
    if (type !== ALL) p.type = type;
    if (qDebounced) p.q = qDebounced;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [category, type, qDebounced, dateFrom, dateTo]);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await api.get(`/workspaces/${workspaceId}/history`, {
        params: { ...buildParams(), limit: PAGE_SIZE, offset },
      });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setCategories(res.data.categories || []);
      setAvailableTypes(res.data.available_types || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, buildParams, offset]);

  useEffect(() => { load(); }, [load]);

  // Un changement de filtre réinitialise la pagination.
  useEffect(() => { setOffset(0); }, [category, type, qDebounced, dateFrom, dateTo]);

  // Les types disponibles dépendent de la catégorie choisie.
  const typeOptions = useMemo(() => {
    if (category === ALL) return availableTypes;
    return availableTypes.filter((t) => t.category === category);
  }, [availableTypes, category]);

  const showCategoryFilter = categories.length > 1;

  const resetFilters = () => {
    setCategory(ALL); setType(ALL); setQ(""); setDateFrom(""); setDateTo("");
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/workspaces/${workspaceId}/history/export.csv`, {
        params: buildParams(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = tr("history:panel.exportFilename", { date: new Date().toISOString().slice(0, 10) });
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tr("history:panel.exportCsvTelecharge"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || tr("history:panel.echecDeLExport"));
    } finally {
      setExporting(false);
    }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="glass rounded-2xl p-6" data-testid="history-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-600" /> {tr("history:panel.journalDuWorkspace")}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {role === "member" && tr("history:panel.evenementsOperationnelsDeVotre")}
            {role === "billing_admin" && tr("history:panel.evenementsDeFacturationDe")}
            {(role === "owner" || role === "admin") && tr("history:panel.tousLesEvenementsDe")}
          </p>
        </div>
        <Button
          data-testid="history-export-csv-btn"
          onClick={exportCsv}
          disabled={exporting || loading}
          variant="outline"
          className="border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {tr("history:panel.exporterEnCsv")}
        </Button>
      </div>

      {/* Barre de filtres */}
      <div className="grid gap-3 md:grid-cols-12 mb-5" data-testid="history-filters">
        <div className="md:col-span-3">
          <Label className="text-xs text-slate-500">{tr("history:panel.recherche")}</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="history-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr("history:panel.evenementActeur")}
              className="pl-9"
            />
          </div>
        </div>
        {showCategoryFilter && (
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">{tr("history:panel.categorie")}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="history-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{tr("history:panel.toutes")}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_FR[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className={showCategoryFilter ? "md:col-span-3" : "md:col-span-4"}>
          <Label className="text-xs text-slate-500">{tr("history:panel.typeDEvenement")}</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="history-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>{tr("history:panel.tous")}</SelectItem>
              {typeOptions.map((t) => (
                <SelectItem key={t.type} value={t.type}>{tr(`history:events.${t.type}`, { defaultValue: t.label })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500">{tr("history:panel.du")}</Label>
          <Input
            data-testid="history-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500">{tr("history:panel.au")}</Label>
          <Input
            data-testid="history-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-500" data-testid="history-count">
          {total === 0 ? tr("history:panel.aucunEvenement") : tr("history:panel.v0V1SurV2", { v0: pageStart, v1: pageEnd, v2: total })}
        </span>
        <Button
          data-testid="history-reset-filters-btn"
          onClick={resetFilters}
          variant="ghost"
          size="sm"
          className="text-slate-500 hover:text-slate-900"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> {tr("history:panel.reinitialiser")}
        </Button>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-500 py-12 text-center" data-testid="history-empty">
          {tr("history:panel.aucunEvenementNeCorrespond")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="history-table">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">{tr("history:panel.date")}</TableHead>
                <TableHead>{tr("history:panel.categorie")}</TableHead>
                <TableHead>{tr("history:panel.evenement")}</TableHead>
                <TableHead>{tr("history:panel.acteur")}</TableHead>
                <TableHead>{tr("history:panel.details")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.id} data-testid={`history-row-${ev.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDate(ev.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CATEGORY_BADGE[ev.category] || ""}>
                      {CATEGORY_FR[ev.category] || ev.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-900 font-medium">{tr(`history:events.${ev.type}`, { defaultValue: ev.label })}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtActor(ev)}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[260px] truncate" title={fmtDetail(ev)}>
                    {fmtDetail(ev) || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            data-testid="history-prev-page"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!canPrev || loading}
            variant="outline" size="sm"
            className="border-slate-200"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> {tr("history:panel.precedent")}
          </Button>
          <Button
            data-testid="history-next-page"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!canNext || loading}
            variant="outline" size="sm"
            className="border-slate-200"
          >
            {tr("history:panel.suivant")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
